import { html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseInput } from "./base-input";
import { globalState } from "../lib/globa-state";
import { Datepicker } from "vanillajs-datepicker";

@customElement("pl-input")
export class Input extends BaseInput {
    @property()
    type: string = "";

    @property()
    pattern: string = "";

    @property()
    min: string = "";

    @property()
    max: string = "";

    @state()
    theme: string = "";

    get validationMessage() {
        return this._inputElement.validationMessage;
    }

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener("keydown", (e: KeyboardEvent) => this._keydown(e));
        globalState.subscribe("theme", this.handleGlobalStateChange.bind(this));
        this.theme = globalState.getState("theme");
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        globalState.unsubscribe("theme", this.handleGlobalStateChange.bind(this));
    }

    firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
        super.firstUpdated(_changedProperties);

        const inputElement = this.shadowRoot?.querySelector("input");
        if (this.type === "date" && inputElement) {
            const date = new Datepicker(inputElement, { format: "dd/mm/yyyy" });

            inputElement.addEventListener("changeDate", () => {
                const selectedDate = date.getDate();
                let formattedDate = "";
                if (selectedDate instanceof Date) {
                    formattedDate = selectedDate.toLocaleDateString("en-GB");
                } else if (Array.isArray(selectedDate) && selectedDate.length > 0 && selectedDate[0] instanceof Date) {
                    formattedDate = selectedDate[0].toLocaleDateString("en-GB");
                }

                this.value = formattedDate;
                const event = new Event("change", {
                    bubbles: true,
                    cancelable: true,
                });
                inputElement.dispatchEvent(event);
            });
        }

        if (this.type === "month" && inputElement) {
            const date = new Datepicker(inputElement, {
                format: "mm/yyyy",
                pickLevel: 1,
            });

            inputElement.addEventListener("changeDate", () => {
                const selectedDate = date.getDate();
                let formattedDate = "";

                // Ensure selectedDate is a Date object
                if (selectedDate instanceof Date) {
                    // Format as mm/yyyy
                    formattedDate = new Intl.DateTimeFormat("en-GB", {
                        month: "2-digit",
                        year: "numeric",
                    }).format(selectedDate);
                } else if (Array.isArray(selectedDate) && selectedDate.length > 0 && selectedDate[0] instanceof Date) {
                    formattedDate = new Intl.DateTimeFormat("en-GB", {
                        month: "2-digit",
                        year: "numeric",
                    }).format(selectedDate[0]);
                }

                this.value = formattedDate;
                const event = new Event("change", {
                    bubbles: true,
                    cancelable: true,
                });
                inputElement.dispatchEvent(event);
            });
        }
    }

    handleGlobalStateChange() {
        this.theme = globalState.getState("theme");
    }

    _keydown(e: KeyboardEvent) {
        if (e.key === "Enter") {
            this.checkValidity();
            this.dispatchEvent(new CustomEvent("enter"));
            e.preventDefault();
            e.stopPropagation();
        } else if (e.key === "Escape") {
            this.dispatchEvent(new CustomEvent("escape"));
            e.preventDefault();
            e.stopPropagation();
        }
    }

    static styles = [
        ...BaseInput.styles,
        css`
            input {
                box-sizing: border-box;
                text-overflow: ellipsis;
                box-shadow: none;
            }

            :host(.edit-input) input {
                padding: 8px;
            }

            :host(.edit-input) input[readonly] {
                padding: 8px;
                opacity: 0.8;
            }

            @supports (-webkit-overflow-scrolling: touch) {
                input[type="date"],
                input[type="month"] {
                    display: block;
                    min-height: 1.5em;
                }
            }

            .datepicker {
                width: -moz-min-content;
                width: min-content;
            }

            .datepicker:not(.active) {
                display: none;
            }

            .datepicker-dropdown {
                position: absolute;
                z-index: 20;
                padding-top: 4px;
            }

            .datepicker-dropdown.datepicker-orient-top {
                padding-top: 0;
                padding-bottom: 4px;
            }

            .datepicker-picker {
                display: flex;
                flex-direction: column;
                border-radius: 4px;
                background-color: var(--menu-background);
            }

            .datepicker-dropdown .datepicker-picker {
                box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
            }

            .datepicker-main {
                flex: auto;
                padding: 2px;
            }

            .datepicker-footer {
                box-shadow: inset 0 1px 1px rgba(10, 10, 10, 0.1);
                background-color: hsl(0, 0%, 96%);
            }

            .datepicker-title {
                box-shadow: inset 0 -1px 1px rgba(10, 10, 10, 0.1);
                background-color: hsl(0, 0%, 96%);
                padding: 0.375rem 0.75rem;
                text-align: center;
                font-weight: 700;
            }

            .datepicker-controls {
                display: flex;
            }

            .datepicker-header .datepicker-controls {
                padding: 2px 2px 0;
            }

            .datepicker-controls .button {
                display: inline-flex;
                position: relative;
                align-items: center;
                justify-content: center;
                margin: 0;
                border: 1px solid gainsboro;
                border-radius: 4px;
                box-shadow: none;
                background-color: var(--menu-background);
                cursor: pointer;
                padding: calc(0.375em - 1px) 0.75em;
                height: 2.25em;
                vertical-align: top;
                text-align: center;
                line-height: 1.5;
                white-space: nowrap;
                color: inherit;
                font-size: 1rem;
            }

            .datepicker-controls .button:focus,
            .datepicker-controls .button:active {
                outline: none;
            }

            .datepicker-controls .button:hover {
                border-color: #b8b8b8;
                color: inherit;
            }

            .datepicker-controls .button:focus {
                border-color: hsl(217, 71%, 53%);
                color: inherit;
            }

            .datepicker-controls .button:focus:not(:active) {
                box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
            }

            .datepicker-controls .button:active {
                border-color: #474747;
                color: inherit;
            }

            .datepicker-controls .button[disabled] {
                cursor: not-allowed;
            }

            .datepicker-header .datepicker-controls .button {
                border-color: transparent;
                font-weight: bold;
            }

            .datepicker-header .datepicker-controls .button:hover {
                background-color: var(--color-shade-1);
            }

            .datepicker-header .datepicker-controls .button:active {
            }

            .datepicker-footer .datepicker-controls .button {
                flex: auto;
                margin: calc(0.375rem - 1px) 0.375rem;
                border-radius: 2px;
                font-size: 0.75rem;
            }

            .datepicker-controls .view-switch {
                flex: auto;
            }

            .datepicker-controls .prev-button,
            .datepicker-controls .next-button {
                padding-right: 0.375rem;
                padding-left: 0.375rem;
                flex: 0 0 14.2857142857%;
            }

            .datepicker-controls .prev-button.disabled,
            .datepicker-controls .next-button.disabled {
                visibility: hidden;
            }

            .datepicker-view,
            .datepicker-grid {
                display: flex;
            }

            .datepicker-view {
                align-items: stretch;
                width: 15.75rem;
            }

            .datepicker-grid {
                flex-wrap: wrap;
                flex: auto;
            }

            .datepicker .days {
                display: flex;
                flex-direction: column;
                flex: auto;
            }

            .datepicker .days-of-week {
                display: flex;
            }

            .datepicker .week-numbers {
                display: flex;
                flex-direction: column;
                flex: 0 0 9.6774193548%;
            }

            .datepicker .weeks {
                display: flex;
                flex-direction: column;
                align-items: stretch;
                flex: auto;
            }

            .datepicker span {
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                cursor: default;
                -webkit-touch-callout: none;
                -webkit-user-select: none;
                -moz-user-select: none;
                user-select: none;
            }

            .datepicker .dow {
                height: 1.5rem;
                font-size: 0.875rem;
                font-weight: 700;
            }

            .datepicker .week {
                flex: auto;
                color: #b8b8b8;
                font-size: 0.75rem;
            }

            .datepicker-cell,
            .datepicker .days .dow {
                flex-basis: 14.2857142857%;
            }

            .datepicker-cell {
                height: 2.25rem;
            }

            .datepicker-cell:not(.day) {
                flex-basis: 25%;
                height: 4.5rem;
            }

            .datepicker-cell:not(.disabled):hover {
                background-color: var(--color-shade-1);
                cursor: pointer;
            }

            .datepicker-cell.focused:not(.selected) {
                background-color: var(--menu-item-selected-background);
            }

            .datepicker-cell.selected,
            .datepicker-cell.selected:hover {
                background-color: var(--color-shade-1);
                font-weight: 600;
            }

            .datepicker-cell.disabled {
                color: gainsboro;
            }

            .datepicker-cell.prev:not(.disabled),
            .datepicker-cell.next:not(.disabled) {
                color: hsl(0, 0%, 48%);
            }

            .datepicker-cell.prev.selected,
            .datepicker-cell.next.selected {
                color: #e6e6e6;
            }

            .datepicker-cell.highlighted:not(.selected):not(.range):not(.today) {
                border-radius: 0;
                background-color: hsl(0, 0%, 96%);
            }

            .datepicker-cell.highlighted:not(.selected):not(.range):not(.today):not(.disabled):hover {
                background-color: var(--color-shade-1);
            }

            .datepicker-cell.highlighted:not(.selected):not(.range):not(.today).focused {
                background-color: var(--menu-item-selected-background);
            }

            .datepicker-cell.today:not(.selected) {
                background-color: hsl(171, 100%, 41%);
            }

            .datepicker-cell.today:not(.selected):not(.disabled) {
                color: #fff;
            }

            .datepicker-cell.today.focused:not(.selected) {
                background-color: #00ccad;
            }

            .datepicker-cell.range-end:not(.selected),
            .datepicker-cell.range-start:not(.selected) {
                background-color: #b8b8b8;
                color: #fff;
            }

            .datepicker-cell.range-end.focused:not(.selected),
            .datepicker-cell.range-start.focused:not(.selected) {
                background-color: #b3b3b3;
            }

            .datepicker-cell.range-start:not(.range-end) {
                border-radius: 4px 0 0 4px;
            }

            .datepicker-cell.range-end:not(.range-start) {
                border-radius: 0 4px 4px 0;
            }

            .datepicker-cell.range {
                border-radius: 0;
                background-color: gainsboro;
            }

            .datepicker-cell.range:not(.disabled):not(.focused):not(.today):hover {
                background-color: var(--color-shade-1);
            }

            .datepicker-cell.range.disabled {
                color: #c6c6c6;
            }

            .datepicker-cell.range.focused {
                background-color: #d1d1d1;
            }

            .datepicker-input.in-edit {
                border-color: #276bda;
            }

            .datepicker-input.in-edit:focus,
            .datepicker-input.in-edit:active {
                box-shadow: 0 0 0.25em 0.25em rgba(39, 107, 218, 0.2);
            }
        `,
    ];

    _renderInput() {
        const { placeholder, readonly, noTab, disabled, autocapitalize, required, type, pattern, maxlength } = this;
        const colorSchemeTheme = this.theme === "light" ? "light" : "dark";
        return html`
            <input
                id=${this._inputId}
                class="input-element"
                .placeholder=${placeholder}
                ?readonly=${readonly}
                .tabIndex=${noTab || readonly ? -1 : NaN}
                ?disabled=${disabled}
                autocapitalize="${autocapitalize ? "on" : "off"}"
                ?required=${required}
                autocomplete="off"
                spellcheck="false"
                autocomplete="off"
                style="color-scheme: ${colorSchemeTheme}"
                maxlength=${maxlength}
                type="${type === "date" || type === "month" ? "text" : (type as any)}"
                .pattern="${pattern || ".*"}"
                .min=${this.min}
                .max=${this.max}
                @focus=${this._focused}
                @blur=${this._blurred}
                @change=${this._changeHandler}
                @touchend=${this._touchend}
            />
        `;
    }

    checkValidity() {
        return this._inputElement && this._inputElement.checkValidity();
    }

    reportValidity() {
        return this._inputElement && this._inputElement.reportValidity();
    }
}
